import React, { useCallback, useEffect, useState } from 'react';
import CourseSelect from './CaddieGames/CourseSelect';
import CourseAddModal from './CaddieGames/CourseAddModal';
import ConfirmModal from './CaddieGames/modals/ConfirmModal';
import { mockCourses } from './CaddieGames/mockCourses';
import { grouplooperCourses } from './CaddieGames/grouplooperCourses';
import { Course } from './CaddieGames/types';
import {
  queryCourses,
  getCourseFacets,
  getCourse,
  createCourse,
  updateCourse,
  deleteCatalogCourse,
  seedCourses,
  listProposals,
  approveProposal,
  rejectProposal,
  CourseProposal,
  enrichCourse,
  EnrichmentResult,
} from './CaddieGames/courseCatalogApi';
import './CaddieGames/caddiegames.css';
import './SuperAdminDashboard.css';

// Review bell — pending-proposal count, 99+ cap, styled like the Tasks bell.
function ReviewBell({
  count,
  active,
  onClick,
}: {
  count: number;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      className={`cg-bell${active ? ' cg-bell-active' : ''}`}
      onClick={onClick}
      aria-label={`${count} pending change${count === 1 ? '' : 's'} to review`}
      title="Pending changes to review"
    >
      🔔
      {count > 0 && (
        <span className="cg-bell-badge">{count > 99 ? '99+' : count}</span>
      )}
    </button>
  );
}

// CaddieGames admin console (CGAdmin org). Manages the SHARED backend course
// catalog (Cosmos) via server-side paginated search, plus the player-submitted
// change queue (proposals). The catalog list is fetched by CourseSelect in
// server mode — no whole-catalog load — so it scales past a few hundred courses.
export default function CGAdminConsole() {
  const [activeTab, setActiveTab] = useState<'courses'>('courses');
  const [error, setError] = useState<string | null>(null);
  const [pendingDelete, setPendingDelete] = useState<Course | null>(null);
  const [seeding, setSeeding] = useState(false);
  const [seedMsg, setSeedMsg] = useState<string | null>(null);
  // Whether the shared catalog is empty — the Seed button only shows then, so
  // it self-documents on a fresh container and stays out of the way once
  // populated (or after Geoff's bulk load).
  const [catalogEmpty, setCatalogEmpty] = useState(false);
  // Bumped to force the catalog list to re-query after an external mutation.
  const [reloadKey, setReloadKey] = useState(0);

  const checkEmpty = useCallback(async () => {
    try {
      const { courses } = await queryCourses({ limit: 1 });
      setCatalogEmpty(courses.length === 0);
    } catch {
      // On error, don't surface Seed (avoid a false "empty").
      setCatalogEmpty(false);
    }
  }, []);
  useEffect(() => {
    checkEmpty();
  }, [checkEmpty]);

  // Moderation queue
  const [proposals, setProposals] = useState<CourseProposal[]>([]);
  const [reviewMode, setReviewMode] = useState(false);
  const [reviewing, setReviewing] = useState<CourseProposal | null>(null);
  // Current DB version of the reviewed course — the diff baseline (edit only).
  const [reviewBaseline, setReviewBaseline] = useState<Course | null>(null);
  const [rejecting, setRejecting] = useState<CourseProposal | null>(null);

  // Enrichment — course currently being enriched, the suggested-changes result
  // under review, and a notice when no official scorecard was found.
  const [enrichingId, setEnrichingId] = useState<string | null>(null);
  const [enrichResult, setEnrichResult] = useState<EnrichmentResult | null>(null);
  const [enrichNotice, setEnrichNotice] = useState<string | null>(null);
  // Course the "no scorecard" notice refers to — enables the paste-a-URL retry.
  const [pasteCourse, setPasteCourse] = useState<Course | null>(null);

  const TABS: { key: typeof activeTab; label: string }[] = [
    { key: 'courses', label: 'Courses' },
  ];

  // Stable callbacks for CourseSelect's server-search mode.
  const serverSearch = useCallback(
    (p: {
      q: string;
      country: string;
      region: string;
      offset: number;
      limit: number;
    }) => queryCourses(p),
    [],
  );
  const loadFacets = useCallback(
    (country: string) => getCourseFacets(country),
    [],
  );

  const loadProposals = useCallback(async () => {
    try {
      const { proposals: p } = await listProposals();
      setProposals(p);
    } catch (e) {
      console.warn('[caddiegames] load proposals failed:', e);
      setProposals([]);
    }
  }, []);

  useEffect(() => {
    loadProposals();
  }, [loadProposals]);

  // Refresh the pending queue when the admin returns to the tab/window.
  useEffect(() => {
    const onFocus = () => loadProposals();
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [loadProposals]);

  // Persist a card to the catalog. CourseSelect re-queries itself via onSaved.
  const persist = async (course: Course, mode: 'create' | 'update') => {
    if (mode === 'update') await updateCourse(course.id, course, 'CGAdmin');
    else await createCourse(course, 'CGAdmin');
  };

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    try {
      await deleteCatalogCourse(pendingDelete.id);
      setPendingDelete(null);
      setReloadKey((k) => k + 1);
    } catch (e) {
      setError((e as Error).message || 'Failed to delete course');
      setPendingDelete(null);
    }
  };

  const runSeed = async () => {
    setSeeding(true);
    setSeedMsg(null);
    try {
      const res = await seedCourses([...mockCourses, ...grouplooperCourses]);
      setSeedMsg(
        `Seeded ${res.created} course(s); skipped ${res.skipped} already present.`,
      );
      setReloadKey((k) => k + 1);
      checkEmpty();
    } catch (e) {
      setSeedMsg((e as Error).message || 'Seed failed');
    } finally {
      setSeeding(false);
    }
  };

  // Open review — for an edit, fetch the current DB card as the yellow-diff
  // baseline (we no longer hold the whole catalog in memory).
  const openReview = async (p: CourseProposal) => {
    setReviewing(p);
    setReviewBaseline(null);
    if (p.kind === 'edit') {
      try {
        setReviewBaseline(await getCourse(p.courseId));
      } catch {
        setReviewBaseline(null);
      }
    }
  };

  // Enrich a course: ask the backend for suggested changes (never writes) and
  // open the review modal. `sourceUrl` (optional) reads that exact scorecard
  // page directly instead of auto-searching. On no source / error, surface a
  // notice that also lets the admin paste a URL.
  const handleEnrich = async (course: Course, sourceUrl?: string) => {
    setEnrichingId(course.id);
    setEnrichNotice(null);
    setPasteCourse(null);
    setError(null);
    try {
      const result = await enrichCourse(course.id, sourceUrl);
      if (
        result.status === 'suggestions' &&
        result.suggested &&
        result.changes.length > 0
      ) {
        setEnrichResult(result);
      } else if (result.status === 'suggestions') {
        // A scorecard was found but it matched the current card exactly.
        setEnrichNotice(
          `Found a scorecard for "${course.name}", but it matches the current card — nothing to change.`,
        );
      } else {
        const checked = result.meta?.checkedSources ?? [];
        setEnrichNotice(
          `No scorecard found for "${course.name}".` +
            (checked.length ? ` Checked: ${checked.join(', ')}.` : ''),
        );
        setPasteCourse(course);
      }
    } catch (e) {
      setError((e as Error).message || 'AI Verify failed');
    } finally {
      setEnrichingId(null);
    }
  };

  const approve = async (proposal: CourseProposal, course: Course) => {
    await approveProposal(proposal.id, course, 'CGAdmin');
    await loadProposals();
    setReloadKey((k) => k + 1);
  };

  const confirmReject = async () => {
    if (!rejecting) return;
    try {
      await rejectProposal(rejecting.id, 'CGAdmin');
      setRejecting(null);
      await loadProposals();
    } catch (e) {
      setError((e as Error).message || 'Failed to reject proposal');
      setRejecting(null);
    }
  };

  const bell = (
    <ReviewBell
      count={proposals.length}
      active={reviewMode}
      onClick={() => setReviewMode((v) => !v)}
    />
  );

  const fmt = (s: string) => {
    try {
      return new Date(s).toLocaleString();
    } catch {
      return s;
    }
  };

  return (
    <div className="platform-admin-dashboard cgadmin-console">
      <div className="sa-sticky-head">
        <div
          style={{
            display: 'flex',
            alignItems: 'baseline',
            gap: 12,
            padding: '0 0 8px',
          }}
        >
          <h2 style={{ margin: 0, fontSize: '1.1rem', color: '#1f2937' }}>
            CaddieGames Admin
          </h2>
          <span style={{ color: '#6b7280', fontSize: '0.8rem' }}>
            Course catalog
          </span>
        </div>
        <div className="sa-tabs">
          {TABS.map((t) => (
            <button
              key={t.key}
              type="button"
              className={`sa-tab${activeTab === t.key ? ' sa-tab-active' : ''}`}
              onClick={() => setActiveTab(t.key)}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      <div className="sa-tab-panel">
        {activeTab === 'courses' && (
          <div className="cg-page">
            {(catalogEmpty || seedMsg) && (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-end',
                  gap: 12,
                  flexWrap: 'wrap',
                  marginBottom: 8,
                }}
              >
                {seedMsg && (
                  <span style={{ color: '#166534', fontSize: '0.85rem' }}>
                    {seedMsg}
                  </span>
                )}
                {catalogEmpty && (
                  <button
                    type="button"
                    className="cg-btn cg-btn-ghost"
                    onClick={runSeed}
                    disabled={seeding}
                    title="Import the shipped course library into the shared catalog (safe to re-run)"
                  >
                    {seeding ? 'Seeding…' : 'Seed catalog'}
                  </button>
                )}
              </div>
            )}

            {error && (
              <p className="cg-select-hint" style={{ color: '#b91c1c' }}>
                {error}
              </p>
            )}

            {enrichNotice && (
              <p
                className="cg-select-hint"
                style={{
                  color: '#92400e',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 12,
                }}
              >
                <span>{enrichNotice}</span>
                <span style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                  {pasteCourse && (
                    <button
                      type="button"
                      className="cg-btn cg-btn-ghost"
                      onClick={() => {
                        const url = window.prompt(
                          `Paste the scorecard URL for "${pasteCourse.name}" (e.g. the BlueGolf detailed scorecard page):`,
                        );
                        if (!url) return;
                        const trimmed = url.trim();
                        if (!/^https?:\/\//i.test(trimmed)) {
                          window.alert(
                            'Please paste a full URL starting with http:// or https://',
                          );
                          return;
                        }
                        handleEnrich(pasteCourse, trimmed);
                      }}
                    >
                      Paste URL
                    </button>
                  )}
                  <button
                    type="button"
                    className="cg-btn cg-btn-link"
                    onClick={() => {
                      setEnrichNotice(null);
                      setPasteCourse(null);
                    }}
                  >
                    Dismiss
                  </button>
                </span>
              </p>
            )}

            {reviewMode ? (
              // Pending review queue
              <div className="cg-select">
                <div className="cg-select-titlerow">
                  <h2 className="cg-select-title">
                    Pending changes ({proposals.length})
                  </h2>
                  {bell}
                </div>
                {proposals.length === 0 ? (
                  <p className="cg-select-hint">
                    No pending changes to review.
                  </p>
                ) : (
                  <ul className="cg-review-list">
                    {proposals.map((p) => (
                      <li key={p.id} className="cg-review-row">
                        <div className="cg-review-info">
                          <div className="cg-review-name">
                            <span
                              className={`cg-review-kind cg-review-kind-${p.kind}`}
                            >
                              {p.kind === 'new' ? 'New' : 'Edit'}
                            </span>
                            {p.proposed.name}
                          </div>
                          <div className="cg-review-meta">
                            {p.submittedByName} · {fmt(p.submittedAt)}
                          </div>
                        </div>
                        <div className="cg-review-actions">
                          <button
                            type="button"
                            className="cg-btn cg-btn-primary"
                            onClick={() => openReview(p)}
                          >
                            Review
                          </button>
                          <button
                            type="button"
                            className="cg-btn cg-btn-danger"
                            onClick={() => setRejecting(p)}
                          >
                            Reject
                          </button>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ) : (
              <CourseSelect
                title="Course catalog"
                showAllWhenEmpty
                mergeLocal={false}
                courses={[]}
                serverSearch={serverSearch}
                loadFacets={loadFacets}
                reloadKey={reloadKey}
                persist={persist}
                onDelete={(c) => setPendingDelete(c)}
                onEnrich={handleEnrich}
                enrichingId={enrichingId}
                headerRight={bell}
              />
            )}
          </div>
        )}
      </div>

      {/* Review a proposal — opens the proposed card editable, changes vs the
          current catalog version highlighted yellow. Save = approve. */}
      {reviewing && (
        <CourseAddModal
          title={reviewing.kind === 'new' ? 'Review new course' : 'Review edit'}
          saveLabel="Approve & publish"
          source="manual"
          courseId={reviewing.courseId}
          compareTo={reviewBaseline ?? undefined}
          initialName={reviewing.proposed.name}
          initialCity={reviewing.proposed.city}
          initialRegion={reviewing.proposed.region}
          initialCountry={reviewing.proposed.country}
          initialLocation={reviewing.proposed.location}
          initialHoles={reviewing.proposed.holes}
          initialTees={reviewing.proposed.tees}
          persist={async (course) => {
            await approve(reviewing, course);
          }}
          onSaved={() => {
            setReviewing(null);
            setReviewBaseline(null);
          }}
          onClose={() => {
            setReviewing(null);
            setReviewBaseline(null);
          }}
        />
      )}

      {/* Review AI enrichment — opens the suggested card editable, with cells
          that differ from the current catalog version highlighted. Save applies
          the change via the normal edit path (nothing was written until now). */}
      {enrichResult && enrichResult.suggested && (
        <CourseAddModal
          title="AI Verify — review changes"
          saveLabel="Approve & apply"
          source="manual"
          courseId={enrichResult.courseId}
          compareTo={enrichResult.current}
          initialName={enrichResult.suggested.name}
          initialCity={enrichResult.suggested.city}
          initialRegion={enrichResult.suggested.region}
          initialCountry={enrichResult.suggested.country}
          initialLocation={enrichResult.suggested.location}
          initialHoles={enrichResult.suggested.holes}
          initialTees={enrichResult.suggested.tees}
          persist={async (course) => {
            await updateCourse(course.id, course, 'CGAdmin');
          }}
          onSaved={() => {
            setEnrichResult(null);
            setReloadKey((k) => k + 1);
          }}
          onClose={() => setEnrichResult(null)}
        />
      )}

      {pendingDelete && (
        <ConfirmModal
          title="Delete course"
          message={`Permanently delete "${pendingDelete.name}" from the shared catalog? This can't be undone.`}
          confirmLabel="Delete"
          variant="danger"
          onConfirm={confirmDelete}
          onCancel={() => setPendingDelete(null)}
        />
      )}

      {rejecting && (
        <ConfirmModal
          title="Reject change"
          message={`Reject this ${
            rejecting.kind === 'new' ? 'new course' : 'edit'
          } from ${rejecting.submittedByName}? It stays only on their device and won't go to the shared catalog.`}
          confirmLabel="Reject"
          variant="danger"
          onConfirm={confirmReject}
          onCancel={() => setRejecting(null)}
        />
      )}
    </div>
  );
}
